// src/components/js/RightSidebar.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../css/RightSidebar.css';

// External reference resources
const externalResources = [
  {
    name: 'NCBI',
    url: 'https://www.ncbi.nlm.nih.gov/',
    logo: `${process.env.PUBLIC_URL}/images/ncbi.jpeg`,
  },
  {
    name: 'EMBL-EBI',
    url: 'https://www.ebi.ac.uk/',
    logo: `${process.env.PUBLIC_URL}/images/embl.png`,
  },
  {
    name: 'Ensembl Plants',
    url: 'https://plants.ensembl.org/index.html',
    logo: `${process.env.PUBLIC_URL}/images/ensembl_logo.png`,
  },
  {
    name: 'UniProt',
    url: 'https://www.uniprot.org/',
    logo: `${process.env.PUBLIC_URL}/images/uniprot.png`,
  },
  {
    name: 'PlantTFDB',
    url: 'https://planttfdb.gao-lab.org/',
    logo: `${process.env.PUBLIC_URL}/images/planttfdb.png`,
  },
];

// ⭐ NEW — Extract dataset title + accession
const extractDatasetAttributes = (activity) => {
  const model = (activity.model || "").toLowerCase();

  const fields = {
    bio_sample: { title: activity.title, accession: activity.accession },
    bio_project: {
      title: activity.project_title,
      accession: activity.bio_project_accession || activity.project_id,
    },
    bioassay: {
      title: activity.aidname || activity.aid_desc,
      accession: activity.aid,
    },
    book: {
      title: activity.title,
      accession: activity.identifiers || activity.entrez_uid,
    },
    gene: {
      title: activity.description || activity.locus_tag,
      accession: activity.locus || activity.gene_id,
    },
    genome_assembly: {
      title: activity.assembly_name,
      accession: activity.assembly_accession,
    },
    geo_platform: { title: activity.title, accession: activity.accession },
    geo_sample: { title: activity.title, accession: activity.accession },
    geo_series: { title: activity.title, accession: activity.accession },
    nucleotide: { title: activity.title, accession: activity.accession },
    pathway: { title: activity.title, accession: activity.accession },
    pmc_article: {
      title: activity.title,
      accession: activity.pmcid || activity.doi,
    },
    protein_uniprot: { title: activity.name, accession: activity.accession },
    protein: {
      title: activity.description,
      accession: activity.product_accession,
    },
    pubmed: { title: activity.title, accession: activity.pmid },
    sequence_metadata: {
      title: activity.chromosome_name,
      accession: activity.assembly_accession,
    },
    sra: {
      title: activity.experiment_title,
      accession: activity.experiment_accession,
    },
    substance: {
      title: activity.record_title,
      accession: activity.record_number,
    },
    taxonomy: {
      title: activity.scientific_name || activity.organism,
      accession: activity.tax_id,
    },
  };

  return (
    fields[model] || {
      title: activity.title || activity.description || null,
      accession: activity.accession || null,
    }
  );
};

// ⭐ NEW — Generate internal dataset link
const makeDatasetLink = (model, accession) => {
  if (!model || !accession) return null;
  return `/${model.toLowerCase()}/${accession}/`;
};

const RightSidebar = () => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const fetchRecentActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('http://197.156.76.122/api/recent-activity/?within=6month');
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setRecentActivity(data);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentActivity();
    const interval = setInterval(fetchRecentActivity, 30000);
    return () => clearInterval(interval);
  }, [fetchRecentActivity]);

  const getActionStyle = (action) => {
    switch ((action || "").toLowerCase()) {
      case "created": return { color: "#22c55e", fontWeight: "bold" };
      case "updated": return { color: "#f59e0b", fontWeight: "bold" };
      case "deleted": return { color: "#ef4444", fontWeight: "bold" };
      case "login":
      case "registered": return { color: "#3b82f6", fontWeight: "bold" };
      default: return { color: "#6b7280" };
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  const toggleShowAll = () => setShowAll((prev) => !prev);
  const visibleActivities = showAll ? recentActivity : recentActivity.slice(0, 5);

  const groupedByDate = visibleActivities.reduce((acc, item) => {
    const date = formatDate(item.timestamp);
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  return (
    <div className="right-sidebar">

      {/* Reference links */}
      <div className="external-links">
        <b><u>Reference Links</u></b>
        <ul className="external-links-list">
          {externalResources.map((res, i) => (
            <li key={i}>
              <a href={res.url} target="_blank" rel="noopener noreferrer">
                <img src={res.logo} alt={`${res.name} logo`} className="external-link-logo" /> {res.name}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <div className="Recent"><b>What's New!</b></div>

        {Object.keys(groupedByDate).length > 0 ? (
          Object.entries(groupedByDate).map(([date, items]) => (
            <div key={date} className="activity-group">
              <h4 className="activity-date">{date}</h4>
              <ul className="activity-list">

                {items.map((activity, index) => {
                  const { title, accession } = extractDatasetAttributes(activity);
                  const link = makeDatasetLink(activity.model, accession);

                  return (
                    <li key={index} className="activity-item">
                      <div className="details">
                        
                        <div className="definition-line">
                          <strong style={getActionStyle(activity.action)}>
                            {activity.action?.toUpperCase()}
                          </strong>{" "}
                          in <b>{activity.model}</b>
                        </div>

                        {title && (
                          <div className="definition-line">
                            {link ? (
                              <a href={link} className="dataset-link">
                                <small><em>{title}</em></small>
                              </a>
                            ) : (
                              <small><em>{title}</em></small>
                            )}
                          </div>
                        )}

                        {accession && (
                          <div className="definition-line">
                            <small>
                              <b>Accession:</b>{" "}
                              {link ? (
                                <a href={link} className="dataset-link">
                                  <em>{accession}</em>
                                </a>
                              ) : (
                                <em>{accession}</em>
                              )}
                            </small>
                          </div>
                        )}

                      </div>
                    </li>
                  );
                })}

              </ul>
            </div>
          ))
        ) : (
          <p className="empty-state">{loading ? "Loading..." : "No recent activity found."}</p>
        )}

        {recentActivity.length > 5 && (
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <button className="refresh-btn" onClick={toggleShowAll}>
              {showAll ? "⬆ Show Less" : "⬇ Show More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RightSidebar;

